import { useQuery } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { FileText, Calendar, Bookmark } from "lucide-react";
import { Paper } from "../shared/schema";
import { useLocation } from "wouter";

export default function PapersSection() {
  const [location] = useLocation();
  const isPapersPage = location === "/papers";
  const { data: papers, isLoading, error } = useQuery<Paper[]>({
    queryKey: ['/api/papers'],
  });

  if (isLoading) {
    return (
      <section id="papers" className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-gray-900 mb-4">Papers Científicos</h3>
            <p className="text-lg text-gray-600">Los últimos avances en investigación de inteligencia artificial</p>
          </div>
          <div className="space-y-6">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="animate-pulse p-6">
                <div className="h-6 bg-gray-300 rounded mb-2"></div>
                <div className="h-4 bg-gray-300 rounded mb-4"></div>
                <div className="h-16 bg-gray-300 rounded"></div>
              </Card>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section id="papers" className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h3 className="text-3xl font-bold text-gray-900 mb-4">Papers Científicos</h3>
            <p className="text-lg text-red-600">Error al cargar los papers científicos</p>
          </div>
        </div>
      </section>
    );
  }

  const samplePapers = [
    {
      id: 1,
      title: "Attention Is All You Need: Revisiting Transformer Architecture in 2024",
      abstract: "Este paper presenta una nueva perspectiva sobre la arquitectura Transformer, explorando optimizaciones recientes y su impacto en el rendimiento de modelos de lenguaje de gran escala...",
      authors: "A. Vaswani, N. Shazeer, et al.",
      source: "ArXiv",
      tags: ["Transformers", "NLP", "Attention"],
      createdAt: new Date("2024-12-15"),
      pdfUrl: "https://arxiv.org/pdf/1706.03762",
    },
    {
      id: 2,
      title: "Constitutional AI: Training AI Systems to be Helpful, Harmless, and Honest",
      abstract: "Presentamos un método para entrenar sistemas de IA que sean útiles, inofensivos y honestos mediante el uso de un conjunto de principios constitucionales que guían el comportamiento del modelo...",
      authors: "Y. Bai, A. Jones, et al.",
      source: "ArXiv",
      tags: ["AI Safety", "RLHF", "Ethics"],
      createdAt: new Date("2024-12-12"),
      pdfUrl: "https://arxiv.org/pdf/2212.08073",
    },
    {
      id: 3,
      title: "Retrieval-Augmented Generation for Knowledge-Intensive NLP Tasks",
      abstract: "Exploramos cómo RAG (Retrieval-Augmented Generation) puede mejorar significativamente el rendimiento en tareas de NLP que requieren conocimiento extensivo, combinando retrieval de información con generación de texto...",
      authors: "P. Lewis, E. Perez, et al.",
      source: "ArXiv",
      tags: ["RAG", "Information Retrieval", "Generation"],
      createdAt: new Date("2024-12-08"),
      pdfUrl: "https://arxiv.org/pdf/2005.11401",
    },
  ];

  const displayPapers = papers && papers.length > 0 ? papers : samplePapers;

  const getSourceColor = (source: string) => {
    /*switch (source.toLowerCase()) {
      case 'arxiv':
        return 'bg-purple-100 text-purple-800';
      case 'nature ai':
        return 'bg-blue-100 text-blue-800';
      case 'icml 2024':
        return 'bg-emerald-100 text-emerald-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }*/
  };

  const getSourceIcon = (source: string) => {
    return <FileText className="w-3 h-3 mr-1" />;
  };

  return (
    <section id="papers" className="py-16 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h3 className="text-3xl font-bold text-gray-900 mb-4">Papers Científicos</h3>
          <p className="text-lg text-gray-600">Los últimos avances en investigación de inteligencia artificial</p>
        </div>

        <div className="space-y-6">
          {displayPapers.slice(0, isPapersPage ? displayPapers.length : 3).map((paper) => (
            <Card key={paper.id} className="bg-white shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
              <CardContent className="p-6">
                <div className="flex flex-col lg:flex-row lg:items-start lg:space-x-6">
                  <div className="flex-1">
                    <div className="flex items-center mb-3">
                      <Badge className={`${getSourceColor(paper.source)} text-xs px-2 py-1 rounded-full font-medium mr-3 flex items-center`}>
                        {getSourceIcon(paper.source)}
                        {paper.source}
                      </Badge>
                      <div className="flex items-center text-gray-500 text-sm">
                        <Calendar className="w-4 h-4 mr-1" />
                        {paper.createdAt ? new Date(paper.createdAt).toLocaleDateString('es-ES') : 'Fecha no disponible'}
                      </div>
                    </div>
                    <h4 className="text-xl font-semibold text-gray-900 mb-3">{paper.title}</h4>
                    <p className="text-gray-600 mb-4">{paper.abstract}</p>
                    <div className="flex flex-wrap items-center gap-2 mb-4">
                      {paper.tags?.map((tag, index) => (
                        <Badge key={index} variant="secondary" className="text-xs">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="text-sm text-gray-600">
                        <strong>Autores:</strong> {paper.authors}
                      </div>
                      <div className="flex space-x-3">
                        {paper.pdfUrl && (
                        <Button variant="ghost" className="text-purple-600 font-medium hover:underline p-0 h-auto"
                          onClick={() => paper.pdfUrl && window.open(paper.pdfUrl, '_blank', 'noopener,noreferrer')}>
                          Ver PDF
                        </Button>)}
                        <Button variant="ghost" size="sm" className="text-gray-600 hover:text-gray-800 p-1 h-auto">
                          <Bookmark className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {!isPapersPage && (
          <div className="text-center mt-12" >
            <Button className="bg-purple-600 text-white px-8 py-3 font-semibold hover:bg-purple-700"
            onClick={() =>
                  window.open(`/papers`, "_self", "noopener,noreferrer")
                }>
              Ver Más Papers
            </Button>
          </div>
        )}
      </div>
    </section>
  );
}
